import React, { useState } from 'react';

import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import AnnualMilesInput from './components/annual-miles-input';
import CalendarLinesRegularIcon from '../../shared/icons/calendar-lines-regular-icon';
import ClockRegularIcon from '../../shared/icons/clock-regular-icon';
import { ArrowsRepeatRegularIcon } from '../../shared/icons/icons';
import TireLifeCounter from '../tire-life-counter/tire-life-counter';

import { useTreadwellPreferences } from '../../shared/hooks/hooks';

import {
  calculateDrivingPreferencesLifeExpectancy,
  getTireAnnualOwnershipCost,
  getTireCostPerMile
} from '../../shared/utils/treadwell/treadwell';

import { ProductData } from '../../shared/types/graphql-types';
import {
  ANNUAL_OWNERSHIP_COST_TOOLTIP,
  MILES_DRIVEN_ANNUALLY_DEFAULT_VALUE
} from '../../shared/constants/treadwell';

import styles from './miles-driven-annually-info.scss';

const LIFE_OF_TIRE_TOOLTIP =
  'We calculate tire life estimates by dividing its median mileage by the number of miles you drive per year.';

interface MilesDrivenAnnuallyInfoProps {
  costPerMile?: number;
  median?: number;
  product?: ProductData;
  showTooltip?: boolean;
}

function MilesDrivenAnnuallyInfo(props: MilesDrivenAnnuallyInfoProps) {
  const {
    costPerMile: costPerMileProp,
    median,
    product,
    showTooltip = false
  } = props;
  const { treadwellPreferences } = useTreadwellPreferences();
  const [milesDrivenAnnually, setMilesDrivenAnnually] = useState(
    Number(
      treadwellPreferences?.selectedDrivingPreferences?.milesDrivenAnnually ||
        MILES_DRIVEN_ANNUALLY_DEFAULT_VALUE
    )
  );

  const costPerMileCalculation = getTireCostPerMile(product);
  const costPerMile = costPerMileProp || costPerMileCalculation;

  const { tireLifeMonths, tireLifeYears } =
    calculateDrivingPreferencesLifeExpectancy(
      product,
      treadwellPreferences,
      median
    );

  const annualOwnershipCost = getTireAnnualOwnershipCost(
    costPerMile,
    milesDrivenAnnually
  );

  return (
    <div
      className={styles['container']}
      data-testid="miles-driven-annually-info"
    >
      <div className={styles['title']}>
        <CalendarLinesRegularIcon className={styles['icon']} />
        <Typography weight="bold">Miles Driven Annually</Typography>
      </div>
      <Typography>
        Tell us how many miles you drive annually to see life of tire and annual
        tire life-cycle expense:
      </Typography>
      <AnnualMilesInput
        onChange={annualMiles => setMilesDrivenAnnually(annualMiles)}
      />
      <div className={styles['tire-life-cost-container']}>
        <div className={styles['tire-life-container']}>
          <div className={styles['title-secondary']}>
            <ClockRegularIcon className={styles['icon']} />
            <Typography weight="bold">Life of Tire</Typography>
            {showTooltip && (
              <Tooltip
                className={styles['tooltip']}
                content={LIFE_OF_TIRE_TOOLTIP}
              />
            )}
          </div>
          <div className={styles['year-month-container']}>
            {typeof tireLifeYears === 'number' && (
              <div>
                <TireLifeCounter countTo={tireLifeYears} isWholeNum start>
                  {({ value }) => (
                    <span
                      className={styles['tire-life-value']}
                      data-testid="miles-driven-annually-info__tire-life-years"
                    >
                      {value}
                    </span>
                  )}
                </TireLifeCounter>
                <span className={styles['tire-life-label']}>years</span>
              </div>
            )}

            <div>
              {typeof tireLifeMonths === 'number' ? (
                <TireLifeCounter countTo={tireLifeMonths} isWholeNum start>
                  {({ value }) => (
                    <span
                      className={styles['tire-life-value']}
                      data-testid="miles-driven-annually-info__tire-life-months"
                    >
                      {value}
                    </span>
                  )}
                </TireLifeCounter>
              ) : (
                <span className={styles['tire-life-value-na']}>N/A</span>
              )}
              <span className={styles['tire-life-label']}>months</span>
            </div>
          </div>
        </div>
        {Boolean(annualOwnershipCost) && (
          <div className={styles['cost-container']}>
            <div className={styles['title-secondary']}>
              <ArrowsRepeatRegularIcon className={styles['icon']} />
              <Typography weight="bold">Annual Ownership Cost</Typography>
              {showTooltip && (
                <Tooltip
                  className={styles['tooltip']}
                  content={ANNUAL_OWNERSHIP_COST_TOOLTIP}
                />
              )}
            </div>
            <Typography className={styles['annual-cost-value']} weight="bold">
              ${annualOwnershipCost.toFixed(2)}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}

export default MilesDrivenAnnuallyInfo;
